import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from "reactstrap";
import { showToastMessage } from "common/service/CommonService";
import { useForm } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { ACCESSIONING_PATHS } from "Routes/paths";
import { useSessionService } from "modules/Session/Session/api/useSession";
import { getNewAccessioningFields } from "../helper/accessioningconstant";
import { useGlobalStudy } from "context/StudyContext";

interface NewAccessioningModalProps {
  isOpen: boolean;
  toggle: () => void;
  redirectPathPattern?: string;
  isNewSessionFlow: boolean;
  onViewSession?: (session: any) => void;
}

type AccessioningFormValues = {
  studycode: string;
  period: string;
  timepoint: string;
  dosingStation?: string;
};

const NewAccessioningModal: React.FC<NewAccessioningModalProps> = ({
  isOpen,
  toggle,
  redirectPathPattern,
  isNewSessionFlow,
  onViewSession,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AccessioningFormValues>({
    defaultValues: {
      studycode: "",
      period: "",
      timepoint: "",
      dosingStation: "",
    },
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  const selectedStudyCode = watch("studycode");
  const selectedPeriod = watch("period");

  useEffect(() => {
    setValue("dosingStation", "");
    setValue("timepoint", "");
  }, [selectedPeriod, setValue]);

  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });

  const studyOptions =
    (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
      label: study.studycode,
      value: study.studyid,
    })) || [];

  const { selectedStudy: globalStudy } = useGlobalStudy();

  useEffect(() => {
    if (globalStudy?.studyid && studyOptions.length > 0 && isOpen) {
      const matched = studyOptions.find(
        (s: any) => String(s.value) === String(globalStudy.studyid)
      );
      if (matched) {
        setValue("studycode", matched.value);
      }
    } else {
      setValue("studycode", "");
    }
  }, [globalStudy?.studyid, setValue, isOpen]);

  const { SelectQuery: studyDetailQuery } = useMasterStudyService({
    id: selectedStudyCode ? Number(selectedStudyCode) : null,
    enabledSelect: true,
  });

  const studyDetail = (studyDetailQuery.data as any)?.data?.[0];

  const periodOptions =
    studyDetail?.periods?.map((p: any) => ({
      label: `${p.period_number}`,
      value: p.period_id,
    })) || [];

  const timepointOptions =
    studyDetail?.samplingpoints?.map((tp: any) => ({
      label: tp,
      value: tp,
    })) || [];

  const selectedPeriodObj = studyDetail?.periods?.find(
    (p: any) => String(p.period_id) === String(selectedPeriod)
  );

  const subjectsPerStation = selectedPeriodObj?.subjectsperstation;

  const dosingStationOptions =
    subjectsPerStation && typeof subjectsPerStation === "object"
      ? Object.keys(subjectsPerStation)
        .sort((a, b) => {
          const numA = parseInt(a.replace(/\D/g, ""), 10) || 0;
          const numB = parseInt(b.replace(/\D/g, ""), 10) || 0;
          return numA - numB;
        })
        .map((key: string) => {
          const numMatch = key.match(/\d+/);
          const stationNum = numMatch ? numMatch[0].padStart(2, "0") : key;
          return {
            label: `Dosing Station ${stationNum}`,
            value: key,
          };
        })
      : [];

  const protocolFields = getNewAccessioningFields("protocol", {
    studyOptions,
  });

  const periodFields = getNewAccessioningFields("period", {
    selectedStudyCode,
    selectedPeriod,
    periodOptions,
    timepointOptions,
    dosingStationOptions: [
      { label: dosingStationOptions.length > 0 ? "Select Dosing Station" : "No Dosing Stations Available", value: "" },
      ...dosingStationOptions
    ]
  });

  const navigate = useNavigate();
  const { InsertMutation, SelectAllQuery } = useSessionService({
    studyid: selectedStudyCode ? Number(selectedStudyCode) : null,
    pagesize: 10,
  });

  const onSubmit = (data: AccessioningFormValues) => {
    const dsVal = data.dosingStation || "";
    const stationNum = dsVal ? (parseInt(dsVal.replace(/\D/g, ""), 10) || dsVal) : null;

    const payload = {
      ...data,
      dosingstationno: stationNum,
      dosingStation: dsVal || undefined,
    };

    if (isNewSessionFlow) {
      if (data.studycode) {
        InsertMutation.mutate(payload, {
          onSuccess: (res: any) => {
            showToastMessage(
              "success",
              res?.message || "Accessioning session initialized successfully",
            );
            navigate(
              (redirectPathPattern || ACCESSIONING_PATHS.SESSION).replace(
                ":id",
                res.data.sessionid,
              ),
            );
          },
          onError: (error: any) => {
            showToastMessage("error", error || "failed to create session");
          },
        });
      }
    } else {
      // Find matching session
      const selectedPeriodObj = studyDetail?.periods?.find(
        (p: any) => String(p.period_id) === String(data.period)
      );
      const selectedPeriodNumber = selectedPeriodObj ? selectedPeriodObj.period_number : null;

      const sessions = SelectAllQuery?.data?.data || [];
      const matchingSession = sessions.find((s: any) => {
        if (s.summarycard) return false;

        const isStudyMatch = Number(s.studyid) === Number(data.studycode);
        const isPeriodMatch =
          Number(s.period_number) === Number(selectedPeriodNumber) ||
          Number(s.periodid) === Number(data.period);
        const isTimepointMatch = String(s.timepoint_number) === String(data.timepoint);

        const isStationMatch =
          !s.dosingstationno ||
          Number(s.dosingstationno) === Number(stationNum);

        return isStudyMatch && isPeriodMatch && isTimepointMatch && isStationMatch;
      });

      if (matchingSession) {
        if (onViewSession) {
          onViewSession(matchingSession);
        }
      } else {
        showToastMessage("error", "No active session found for the selected parameters.");
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="fw-bold border-bottom p-3">
        <div className="d-flex align-items-center justify-content-between w-100 me-3">
          <div className="d-flex align-items-center gap-2">
            <i className="ri-scanner-2-line text-primary fs-20"></i>
            <span className="d-block">
              {isNewSessionFlow ? "New Accessioning Session" : "Select Existing Session"}
            </span>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        <div>
          <div className="mb-3">
            <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "15px" }}>
              Session Parameters
            </h6>
            <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
              Select the study details for the samples you are about to accession.
            </p>
          </div>

          <div className="mb-3">
            <CommonForm
              fields={protocolFields}
              control={control}
              errors={errors}
              columns={1}
            />
          </div>
          <div className="mb-3">
            <CommonForm
              fields={periodFields}
              control={control}
              errors={errors}
              columns={2}
            />
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="secondary" outline onClick={toggle}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={isNewSessionFlow ? InsertMutation.isPending : SelectAllQuery.isFetching}
        >
          {(isNewSessionFlow && InsertMutation.isPending) || (!isNewSessionFlow && SelectAllQuery.isFetching) ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <i className="ri-play-line me-1"></i>
          )}
          {isNewSessionFlow ? "Initialize Session" : "View Session"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default NewAccessioningModal;
